import React, { useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './ManagerContractMobile.css';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Sidebar from './Components/Sidebar/Sidebar';
import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import Header from './Components/Header/BlueHeader';
import SearchBar from './Components/SearchBar/SearchBar';
import ValveAuditTable from './Components/ValveAudit/ValveAuditTable/ValveAuditTable';
import Footer from './Components/Footer/Footer';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { Row, Col, Card } from 'react-bootstrap';
import { FaBuilding, FaCheckCircle, FaTint, FaWrench } from 'react-icons/fa';
import './Components/Pills/Pills.css';

const selectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    minHeight: '48px',
    ':hover': { border: 'none' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 6px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  multiValue: (base, { data }) => ({
    ...base,
    backgroundColor: data.color === 'red' ? '#ffebee' : data.color === 'green' ? '#e8f5e9' : data.color === 'orange' ? '#fff3e0' : '#eef6ff',
    borderRadius: '4px',
    maxWidth: '100%',
  }),
  multiValueLabel: (base, { data }) => ({
    ...base,
    color: data.color === 'red' ? '#dc3545' : data.color === 'green' ? '#28a745' : data.color === 'orange' ? '#e65100' : '#1f5eec',
    fontWeight: 700,
    fontSize: '0.72rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.1,
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 6,
    paddingRight: 4,
  }),
  multiValueRemove: (base, { data }) => ({
    ...base,
    color: data.color === 'red' ? '#dc3545' : data.color === 'green' ? '#28a745' : data.color === 'orange' ? '#e65100' : '#1f5eec',
    paddingLeft: 2,
    paddingRight: 4,
  }),
  dropdownIndicator: (base) => ({ ...base, color: '#999' }),
  clearIndicator: (base) => ({ ...base, color: '#999' }),
};

const conditionOptions = [
  { value: 'OK', label: 'OK', color: 'green' },
  { value: 'Stiff', label: 'Stiff', color: 'orange' },
  { value: 'Seized', label: 'Seized', color: 'red' },
];
const sealOptions = [
  { value: 'Holding Tight', label: 'Tight', color: 'green' },
  { value: 'Passing', label: 'Passing', color: 'red' },
  { value: 'Not closing', label: 'Not Closing', color: 'red' },
];
const replacementOptions = [
  { value: 'OK', label: 'No', color: 'green' },
  { value: 'Needs', label: 'Needs Replacement', color: 'red' },
];

/* Stacked horizontal bar — shows proportions visually */
function StackedBar({ segments }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return <div style={{ height: 10, borderRadius: 6, backgroundColor: '#e9ecef' }} />;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', backgroundColor: '#e9ecef' }}>
        {segments.filter(s => s.value > 0).map((seg, i) => {
          const pct = (seg.value / total) * 100;
          return (
            <div key={i}
              style={{ width: `${pct}%`, backgroundColor: seg.color, cursor: 'pointer', transition: 'opacity 0.15s', opacity: hoveredIdx !== null && hoveredIdx !== i ? 0.4 : 1 }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            />
          );
        })}
      </div>
      {hoveredIdx !== null && (() => {
        const seg = segments.filter(s => s.value > 0)[hoveredIdx];
        const pct = Math.round((seg.value / total) * 100);
        return (
          <div style={{
            position: 'absolute', top: -32, left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(15,23,42,0.92)', color: '#fff', padding: '3px 10px',
            borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
            pointerEvents: 'none', zIndex: 10,
          }}>
            {seg.label}: {seg.value} ({pct}%)
          </div>
        );
      })()}
    </div>
  );
}

/* Compact status pill — hero count + stacked bar. Hover the bar for the full breakdown. */
function StatusPill({
  title, titleClass, icon, iconClass,
  issueCount, issueLabel, okCount, okLabel = 'OK',
  segments, total,
}) {
  const pct = total > 0 ? Math.round((issueCount / total) * 100) : 0;
  return (
    <Card className="shadow-sm border-0 pill-card h-100">
      <Card.Body className="pill-body pill-body-status d-flex flex-column">
        <div className="pill-header-row">
          <Card.Title className={`pill-title ${titleClass || ''} mb-0`}>{title}</Card.Title>
          <div className={`pill-icon ${iconClass} pill-icon-inline`}>{icon}</div>
        </div>

        <div className="pill-status-hero">
          <div className="pill-status-num">{issueCount}</div>
          <div className="pill-status-meta">
            <div className="pill-status-label">{issueLabel}</div>
            <div className="pill-status-sub">{pct}% of {total}</div>
          </div>
        </div>

        <StackedBar segments={segments} />

        <div className="pill-status-footer">
          <span className="pill-status-dot" style={{ backgroundColor: '#28a745' }} />
          <span className="pill-status-ok"><b>{okCount}</b> {okLabel}</span>
        </div>
      </Card.Body>
    </Card>
  );
}

/* Breakdown pill — bulleted list (green/orange/red) without a big hero number */
function BreakdownPill({ title, titleClass, icon, iconClass, items, total }) {
  return (
    <Card className="shadow-sm border-0 pill-card h-100">
      <Card.Body className="pill-body pill-body-breakdown d-flex flex-column">
        <div className="pill-header-row">
          <Card.Title className={`pill-title ${titleClass || ''} mb-0`}>{title}</Card.Title>
          <div className={`pill-icon ${iconClass} pill-icon-inline`}>{icon}</div>
        </div>
        <div className="pill-breakdown-list">
          {items.map((it) => {
            const pct = total > 0 ? Math.round((it.value / total) * 100) : 0;
            return (
              <div key={it.label} className="pill-breakdown-row">
                <span className="pill-breakdown-dot" style={{ backgroundColor: it.color }} />
                <span className="pill-breakdown-label">{it.label}</span>
                <span className="pill-breakdown-count">{it.value}</span>
                <span className="pill-breakdown-pct">{pct}%</span>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
}

function ValveAudit() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeKey, setActiveKey] = useState('valveAudit');
  const [buildingName, setBuildingName] = useState('');
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState('');
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [valveStats, setValveStats] = useState({});
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [conditionFilter, setConditionFilter] = useState([]);
  const [sealFilter, setSealFilter] = useState([]);
  const [replacementFilter, setReplacementFilter] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('tagNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || '';
  let buildingId = '';
  if (userStr) { try { buildingId = JSON.parse(userStr).buildingId || ''; } catch (_) {} }

  const handleSidebarToggle = () => setSidebarCollapsed(!sidebarCollapsed);
  const handleNavigate = (key) => {
    setActiveKey(key);
    const routes = { hvac: '/', mech: '/mechanical-room', dryerVents: '/dryer-vents', stackCleaning: '/stack-cleaning', waterSavings: '/water-savings', projects: '/projects', valveAudit: '/valve-audit' };
    if (routes[key]) navigate(routes[key]);
  };

  useEffect(() => {
    if (!buildingId || !token) { setIsLoading(false); return; }
    (async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/getBuildingById`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ buildingId }) });
        if (!resp.ok) throw new Error();
        const data = await resp.json();
        setBuildingName(data.name || 'Unknown');
        setBuildingManagerName(Object.prototype.hasOwnProperty.call(data, 'manager') ? data.manager || '' : data.managerProfile?.manager || '');
        setBuildingPhotoUrl(data.photo_url || '');
        const contracts = data.contracts || [];
        setHasValveAuditContract(contracts.includes('valve-audit'));
        setHasProjectContract(contracts.includes('project'));
      } catch (e) { console.error(e); }
      finally { setIsLoading(false); }
    })();
  }, [buildingId, token]);

  useEffect(() => {
    if (!hasValveAuditContract || !buildingId || !token) return;
    const mid = `${buildingId}-valve`;
    setIsDataLoading(true);
    (async () => {
      try {
        const [dR, tR] = await Promise.all([
          fetch(`${API_BASE_URL}/api/getDashboardData`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ maintenanceId: mid, dataFormatVersion: 'valve_audit' }) }),
          fetch(`${API_BASE_URL}/api/getTableData`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ maintenanceId: mid, limit: 2000, skip: 0, dataFormatVersion: 'valve_audit' }) }),
        ]);
        if (dR.ok) { const d = await dR.json(); setValveStats(d.valveStats || {}); }
        if (tR.ok) { const t = await tR.json(); setRows(t.unitsData || []); }
      } catch (e) { console.error(e); }
      finally { setIsDataLoading(false); }
    })();
  }, [hasValveAuditContract, buildingId, token]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const term = searchTerm.toLowerCase().trim();
      let searchMatch = true;
      if (term) {
        const tagNum = (row.tagNumber || '').replace(/[^0-9]/g, '');
        searchMatch = tagNum.includes(term) ||
          [row.tagNumber, row.locationCategory, row.locationDetails, row.valveServicing, row.technician, row.additionalNotes, row.valveIssueDesc, row.sealIssueDesc]
            .some(f => (f || '').toLowerCase().includes(term));
      }
      const condMatch = conditionFilter.length === 0 || conditionFilter.some(f => (row.valveCondition || '').includes(f.value));
      const sealMatch = sealFilter.length === 0 || sealFilter.some(f => (row.sealCondition || '').includes(f.value));
      const replMatch = replacementFilter.length === 0 || replacementFilter.some(f => (row.replacementStatus || '').includes(f.value));
      return searchMatch && condMatch && sealMatch && replMatch;
    });
  }, [rows, searchTerm, conditionFilter, sealFilter, replacementFilter]);

  const sortedData = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      let vA = a[sortColumn], vB = b[sortColumn];
      if (sortColumn === 'tagNumber') { const n = s => { const m = (s || '').match(/(\d+)/); return m ? parseInt(m[1], 10) : 0; }; vA = n(vA); vB = n(vB); }
      if (vA == null) vA = ''; if (vB == null) vB = '';
      if (typeof vA === 'string') vA = vA.toLowerCase(); if (typeof vB === 'string') vB = vB.toLowerCase();
      if (vA < vB) return sortDirection === 'asc' ? -1 : 1;
      if (vA > vB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRows, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = sortedData.slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [rowsPerPage, searchTerm, conditionFilter, sealFilter, replacementFilter]);

  const handleSort = (col) => { setCurrentPage(1); if (sortColumn === col) setSortDirection(p => p === 'asc' ? 'desc' : 'asc'); else { setSortColumn(col); setSortDirection('asc'); } };

  const renderSidebar = () => (
    <div className="dashboard-sidebar-host" onMouseEnter={() => setSidebarCollapsed(false)} onMouseLeave={() => setSidebarCollapsed(true)} style={{ height: '100vh' }}>
      <Sidebar activeKey={activeKey} onNavigate={handleNavigate} collapsed={sidebarCollapsed} onToggle={handleSidebarToggle} buildingName={buildingName} managerName={buildingManagerName} buildingPhotoUrl={buildingPhotoUrl} showProjectsTab={hasProjectContract} showValveAuditTab={hasValveAuditContract} />
    </div>
  );
  const renderNav = () => (
    <>
      <DashboardNavbar logoutInSidebar selectedMaintenance={{ maintenanceID: `${buildingId}-valve` }} buildingName={buildingName} buildingId={buildingId} contractLabel="Valve Audit" />
      <Header maintenanceCycles={[]} selectedCycle="" setSelectedCycle={() => {}} selectedMaintenance={null} contractLabel="Valve Audit" hideMaintenanceCycle={true} valveAuditMode={true} />
    </>
  );
  const mainStyle = { flex: 1, marginLeft: sidebarCollapsed ? '72px' : '240px', transition: 'margin-left 240ms' };

  if (isLoading) {
    return (<div style={{ display: 'flex' }}>{renderSidebar()}<div className="dashboard-content manager-contract-page" style={mainStyle}>{renderNav()}<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}><Spinner animation="border" style={{ color: '#224982' }} /></div></div></div>);
  }
  if (!hasValveAuditContract) {
    return (<div style={{ display: 'flex' }}>{renderSidebar()}<div className="dashboard-content manager-contract-page" style={mainStyle}>{renderNav()}<Alert variant="warning" style={{ width: '90%', margin: '2rem auto' }}>Valve Audit isn't enabled for this building.</Alert><Footer /></div></div>);
  }

  const vs = valveStats;
  const total = vs.totalUnits || 0;
  const okCount = vs.valveOk || 0;
  const stiffCount = vs.valveStiff || 0;
  const seizedCount = vs.valveSeized || 0;
  const sealTight = vs.sealTight || 0;
  const sealPassing = vs.sealPassing || 0;
  const sealNotClosing = vs.sealNotClosing || 0;
  const replOk = vs.replacementOk || 0;
  const replNeeds = vs.needsReplacement || 0;

  const replPct = total > 0 ? Math.round((replNeeds / total) * 100) : 0;
  const replOkPct = total > 0 ? Math.round((replOk / total) * 100) : 0;
  const replColor = '#dc3545';

  const renderSummary = () => (
    <div className="pills-container">
      <Row className="g-3 mb-4">
        {/* Total Valves */}
        <Col xs={12} sm={6} className="pill-col-wide">
          <Card className="shadow-sm border-0 pill-card pill-card-total h-100">
            <Card.Body className="pill-body pill-body-total d-flex flex-column">
              <div className="pill-header-row pill-header-row-total">
                <Card.Title className="pill-title pill-title-total mb-0">Total Valves</Card.Title>
                <div className="pill-icon pill-icon-total pill-icon-inline"><FaBuilding /></div>
              </div>

              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ fontSize: '2.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{total}</span>
                <span style={{ fontSize: '0.95rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>valves inspected</span>
              </div>

              <div className="pill-total-health">
                <div className="pill-total-health-row">
                  <span className="pill-total-health-label">Needs Replacement</span>
                  <span className="pill-total-health-value" style={{ color: replColor }}>
                    {replPct}% <span className="pill-total-health-sublabel">· {replNeeds} valves</span>
                  </span>
                </div>
                <div className="pill-total-health-track">
                  <div className="pill-total-health-fill" style={{ width: `${replPct}%`, backgroundColor: replColor }} />
                </div>
                <div className="pill-total-health-hint">
                  Based on valve operability and seal integrity
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Replacement */}
        <Col xs={12} sm={6} className="pill-col-wide">
          <Card className="shadow-sm border-0 pill-card pill-card-total h-100">
            <Card.Body className="pill-body pill-body-total d-flex flex-column">
              <div className="pill-header-row pill-header-row-total">
                <Card.Title className="pill-title pill-title-health mb-0">Replacement</Card.Title>
                <div className="pill-icon pill-icon-health pill-icon-inline"><FaCheckCircle /></div>
              </div>

              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ fontSize: '2.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{replNeeds}</span>
                <span style={{ fontSize: '0.95rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>need replacement</span>
              </div>

              <div className="pill-total-health">
                <div className="pill-repl-footer">
                  <span className="pill-repl-footer-item">
                    <span className="pill-status-dot" style={{ backgroundColor: '#28a745' }} />
                    <span><b>{replOk}</b> No</span>
                  </span>
                  <span className="pill-repl-footer-item">
                    <span><b>{replNeeds}</b> Needs Replacement</span>
                    <span className="pill-status-dot" style={{ backgroundColor: '#dc3545' }} />
                  </span>
                </div>
                <StackedBar segments={[
                  { value: replOk, color: '#28a745', label: 'No' },
                  { value: replNeeds, color: '#dc3545', label: 'Needs Replacement' },
                ]} />
                <div className="pill-total-health-hint">
                  Recommended when a valve is seized or a seal fails to hold
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Seal Integrity */}
        <Col xs={12} sm={6} className="pill-col-narrow">
          <BreakdownPill
            title="Seal Integrity"
            titleClass="pill-title-ok"
            iconClass="pill-icon-ok"
            icon={<FaTint />}
            total={total}
            items={[
              { label: 'Tight', value: sealTight, color: '#28a745' },
              { label: 'Passing', value: sealPassing, color: '#fd7e14' },
              { label: 'Not Closing', value: sealNotClosing, color: '#dc3545' },
            ]}
          />
        </Col>

        {/* Valve Operability */}
        <Col xs={12} sm={6} className="pill-col-narrow">
          <BreakdownPill
            title="Valve Operability"
            titleClass="pill-title-found"
            iconClass="pill-icon-found"
            icon={<FaWrench />}
            total={total}
            items={[
              { label: 'OK', value: okCount, color: '#28a745' },
              { label: 'Stiff', value: stiffCount, color: '#fd7e14' },
              { label: 'Seized', value: seizedCount, color: '#dc3545' },
            ]}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ display: 'flex' }}>
      {renderSidebar()}
      <div className="dashboard-content manager-contract-page" style={mainStyle}>
        {renderNav()}
        {isDataLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}><Spinner animation="border" style={{ color: '#224982' }} /></div>
        ) : (
          <>
            {renderSummary()}
            <div className="manager-contract-filters" style={{ width: '90%', margin: '1rem auto', display: 'flex', gap: '1rem', flexWrap: 'nowrap' }}>
              <div style={{ flex: 4 }}><SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} /></div>
              <div style={{ flex: 1.9 }}><Select isMulti options={conditionOptions} value={conditionFilter} onChange={v => setConditionFilter(v || [])} placeholder="Valve Condition..." styles={selectStyles} closeMenuOnSelect={false} components={{ IndicatorSeparator: () => null }} /></div>
              <div style={{ flex: 1.7 }}><Select isMulti options={sealOptions} value={sealFilter} onChange={v => setSealFilter(v || [])} placeholder="Seal..." styles={selectStyles} closeMenuOnSelect={false} components={{ IndicatorSeparator: () => null }} /></div>
              <div style={{ flex: 2.4 }}><Select isMulti options={replacementOptions} value={replacementFilter} onChange={v => setReplacementFilter(v || [])} placeholder="Replacement..." styles={selectStyles} closeMenuOnSelect={false} components={{ IndicatorSeparator: () => null }} /></div>
            </div>
            <ValveAuditTable
              rows={pageData} sortedData={sortedData}
              currentPage={currentPage} totalPages={totalPages}
              rowsPerPage={rowsPerPage} startIndex={startIndex} endIndex={endIndex}
              sortColumn={sortColumn} sortDirection={sortDirection}
              handleSort={handleSort}
              handlePrevPage={() => setCurrentPage(p => Math.max(1, p - 1))}
              handleNextPage={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              handleRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
            />
          </>
        )}
        <Footer />
      </div>
    </div>
  );
}

export default ValveAudit;
